/**
 * Hip spine nomogram top-level composition.
 *
 * The nomogram is a 2D chart with:
 * - X-axis: cup inclination (degrees)
 * - Y-axis: cup anteversion (degrees)
 *
 * It displays:
 * - Customisable angle range bands (inclination + anteversion)
 * - A safe zone polygon defined by the overlap of the two range bands,
 *   clipped to the area between CSI upper and lower bound curves when available
 * - A marker for the current cup position (anteversion + inclination)
 * - A combined version target line
 *
 * Pure formulas, geometry, and line generators live in sibling modules:
 * - ./hipSpineNomogramCalculations — landmark-driven scalar formulas (PFA, APP angle, CSI anteversion)
 * - ./hipSpineNomogramTypes        — shared types and axis constants
 * - ./hipSpineNomogramGeometry     — line interpolation and safe-zone polygon geometry
 * - ./hipSpineNomogramLines        — CSI bound and combined-version line generators
 *
 * The public nomogram API is re-exported here so consumers only need to
 * import from `@/domain/anatomy/pelvis/hipSpineNomogram`.
 */

import { type LpsCoordinateSystem } from '@/domain/anatomy/LPS';
import {
    CSI_LOWER_BOUND,
    CSI_UPPER_BOUND,
    type HipSpineSafeZone,
    type NomogramMarker,
} from './hipSpineNomogramCalculations';
import { type CSIBounds, type NomogramData, type NomogramLine } from './hipSpineNomogramTypes';
import {
    computeSafeZonePolygon,
    isMarkerInSafeZonePolygon,
} from './hipSpineNomogramGeometry';
import {
    computeCSIBound,
    computeCombinedVersionTargetLine,
} from './hipSpineNomogramLines';

export {
    type CSIBounds,
    type ClippedPoint,
    type NomogramData,
    type NomogramLine,
} from './hipSpineNomogramTypes';

export {
    clipCSICurveToSafeZone,
    computeSafeZonePolygon,
    convertCupAngles,
    projectAnteversionAtInclination,
    projectInclinationAtAnteversion,
    projectSafeZonePolygonAtAnteversion,
    projectSafeZonePolygonAtInclination,
} from './hipSpineNomogramGeometry';

export {
    computeCSIBound,
    computeCombinedVersionTargetLine,
} from './hipSpineNomogramLines';

/** Combined version target clamping range in the standing/FPP frame (degrees). */
const MIN_COMBINED_VERSION_TARGET = 20;
const MAX_COMBINED_VERSION_TARGET = 28;

/**
 * Compute all data needed to render the hip spine nomogram.
 *
 * The safe zone is the intersection of the angle range rectangle and
 * the area between the CSI upper/lower bound curves (when available).
 */
export function computeNomogramData(params: {
    targetCS: LpsCoordinateSystem;
    angleRanges: HipSpineSafeZone | null;
    currentAnteversion: number | null;
    currentInclination: number | null;
    /** Target combined version anteversion in the standing/FPP frame (degrees), if available */
    combinedVersionTarget: number | null;
    /** Pelvic femoral angle at the HJC vertex */
    pelvicFemoralAngle: number;
    /** APP tilt angle in degrees (from hip-spine standing X-ray landmarks) */
    appAngle: number;
    /** APP coordinate system (basis vectors in CT-LPS space) */
    appCS: LpsCoordinateSystem;
}): NomogramData {
    const marker: NomogramMarker | null =
        params.currentAnteversion !== null && params.currentInclination !== null
            ? {
                  anteversion: params.currentAnteversion,
                  inclination: params.currentInclination,
              }
            : null;

    const clampedTarget =
        params.combinedVersionTarget !== null
            ? Math.min(
                  MAX_COMBINED_VERSION_TARGET,
                  Math.max(MIN_COMBINED_VERSION_TARGET, params.combinedVersionTarget),
              )
            : null;

    const combinedVersionTarget: NomogramLine | null =
        clampedTarget !== null
            ? computeCombinedVersionTargetLine(clampedTarget, params.appAngle, params.appCS, params.targetCS)
            : null;

    const csiBounds: CSIBounds = {
        upper: computeCSIBound(CSI_UPPER_BOUND, params.pelvicFemoralAngle, params.appAngle, params.appCS, params.targetCS),
        lower: computeCSIBound(CSI_LOWER_BOUND, params.pelvicFemoralAngle, params.appAngle, params.appCS, params.targetCS),
    };

    const safeZonePolygon = params.angleRanges
        ? computeSafeZonePolygon(params.angleRanges, csiBounds.upper, csiBounds.lower)
        : null;

    return {
        angleRanges: params.angleRanges,
        marker,
        isMarkerInSafeZone: isMarkerInSafeZonePolygon(marker, safeZonePolygon),
        combinedVersionTarget,
        csiBounds,
        safeZonePolygon,
    };
}
